'use client';

import { useTranslations } from 'next-intl';
import { AlertCircle, Package, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Link, useRouter } from '@/i18n/routing';
import type { Shipment } from '../types';

type ReceptionStatus = 'matched' | 'processing' | 'not_matched' | 'pending';

const RECEPTION_STATUS_STYLES: Record<
  ReceptionStatus,
  { dot: string; badge: string }
> = {
  matched: {
    dot: 'bg-green-500',
    badge:
      'bg-green-50 text-green-700 hover:bg-green-50 dark:bg-green-950 dark:text-green-300'
  },
  processing: {
    dot: 'bg-amber-500',
    badge:
      'bg-amber-50 text-amber-700 hover:bg-amber-50 dark:bg-amber-950 dark:text-amber-300'
  },
  not_matched: {
    dot: 'bg-red-500',
    badge:
      'bg-red-50 text-red-700 hover:bg-red-50 dark:bg-red-950 dark:text-red-300'
  },
  pending: {
    dot: 'bg-gray-400',
    badge:
      'bg-gray-100 text-gray-600 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300'
  }
};

function getReceptionStatus(shipment: Shipment): ReceptionStatus {
  if (shipment.active_session) return 'processing';
  if (shipment.latest_session?.status === 'completed') {
    return shipment.latest_session.count_status === 'mismatched'
      ? 'not_matched'
      : 'matched';
  }
  return 'pending';
}

interface ReceptionCompactRowProps {
  shipment: Shipment;
  onDelete: (shipment: Shipment) => void;
}

export function ReceptionCompactRow({
  shipment,
  onDelete
}: ReceptionCompactRowProps) {
  const t = useTranslations('reception');
  const router = useRouter();

  const status = getReceptionStatus(shipment);
  const styles = RECEPTION_STATUS_STYLES[status];
  const isUrgent = shipment.priority === 'urgent';
  const isInProgress = !!shipment.active_session;

  const receptionStatusLabels: Record<ReceptionStatus, string> = {
    matched: t('metricMatched'),
    processing: t('metricProcessing'),
    not_matched: t('metricNotMatched'),
    pending: t('statusPending')
  };

  return (
    <div className='flex items-center gap-3 rounded-md border px-4 py-2.5'>
      {/* Left: shipment_number + judicial_entity */}
      <div className='min-w-0 flex-1'>
        <div className='flex items-center gap-2'>
          {isUrgent && (
            <AlertCircle className='h-3.5 w-3.5 shrink-0 animate-pulse text-red-500' />
          )}
          <Link
            href={`/dashboard/shipment-statement/${shipment.id}`}
            className={cn(
              'truncate font-mono font-medium hover:underline',
              isUrgent ? 'text-red-600 dark:text-red-400' : 'text-primary'
            )}
          >
            {shipment.shipment_number}
          </Link>
          <span className='text-muted-foreground hidden truncate text-sm sm:inline'>
            {shipment.judicial_entity}
          </span>
        </div>
      </div>

      {/* Center: box_count (hidden on mobile) */}
      <span className='text-muted-foreground hidden items-center gap-1 text-sm md:flex'>
        <Package className='h-3.5 w-3.5' />
        {shipment.box_count}
      </span>

      {/* Right: status badge */}
      <Badge
        variant='secondary'
        className={cn('shrink-0 gap-1.5', styles.badge)}
      >
        <span className={cn('h-2 w-2 rounded-full', styles.dot)} />
        {receptionStatusLabels[status]}
      </Badge>

      {/* Action buttons */}
      <div className='flex shrink-0 items-center gap-1'>
        {shipment.active_session ? (
          <Button
            size='sm'
            className='h-7'
            onClick={() =>
              router.push(
                `/dashboard/shipment-statement/${shipment.id}/continue`
              )
            }
          >
            {t('followUp')}
          </Button>
        ) : shipment.latest_session?.status === 'completed' ? (
          <Button
            variant='outline'
            size='sm'
            className='h-7'
            onClick={() =>
              router.push(`/dashboard/shipment-statement/${shipment.id}`)
            }
          >
            {t('view')}
          </Button>
        ) : (
          <Button
            size='sm'
            className='h-7'
            onClick={() =>
              router.push(
                `/dashboard/shipment-statement/${shipment.id}/continue`
              )
            }
          >
            {t('initialize')}
          </Button>
        )}
        <Button
          variant='ghost'
          size='icon'
          disabled={isInProgress}
          onClick={() => onDelete(shipment)}
          className='text-muted-foreground hover:text-destructive h-7 w-7'
        >
          <Trash2 className='h-3.5 w-3.5' />
        </Button>
      </div>
    </div>
  );
}
